/**
 * PDF2 Slide 5 - Projeção de Metas
 * Endpoint: /api/v1/visitas/dashboards/metas/
 * 
 * Filtros: ANO apenas (título dinâmico, não "2025" fixo)
 */
import { useState, useEffect } from "react";
import { Card, Row, Col, Spinner, Alert, Table, Badge } from "react-bootstrap";
import { ResponsiveLine } from "@nivo/line";
import api from "../../../services/api";
import { traduzirMesesEmDados } from "./utils/mesesUtils";

const Slide9ProjecaoMetas = ({ programaId, filters }) => {
  const [data, setData] = useState({ metas: [], realizado: [] });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const ano = filters?.year || new Date().getFullYear();

  useEffect(() => {
    api.get(`/visitas/dashboards/metas/?programa_id=${programaId}&ano=${ano}`)
      .then(res => {
        if (res.data) {
          setData(res.data);
        } else {
          setData({ metas: [], realizado: [] });
        }
      })
      .catch((err) => {
        console.error('[Slide9] API error:', err.response?.data || err.message);
        setError(err.response?.data?.error || err.message);
      })
      .finally(() => setLoading(false));
  }, [programaId, ano]);

  const labelAno = `Ano: ${ano}`;

  if (loading) return <Spinner animation="border" className="d-block mx-auto my-4" />;

  if (error) {
    return (
      <Alert variant="warning" className="m-3">
        <strong>Seção 8 - Projeção de Metas:</strong> {error}
      </Alert>
    );
  }

  const metas = Array.isArray(data?.metas) ? data.metas : [];
  const realizado = Array.isArray(data?.realizado) ? data.realizado : [];
  if (data && (data.metas !== undefined && !Array.isArray(data.metas))) {
    console.warn('[Slide9] metas não é array:', data.metas);
  }
  if (data && (data.realizado !== undefined && !Array.isArray(data.realizado))) {
    console.warn('[Slide9] realizado não é array:', data.realizado);
  }
  const hasMetas = metas.length > 0;
  const hasRealizado = realizado.length > 0;

  const metasTraduzidas = traduzirMesesEmDados(metas);
  const realizadoTraduzido = traduzirMesesEmDados(realizado);

  const calcularTrimestres = () => {
    const trimestres = [];
    for (let i = 0; i < metasTraduzidas.length; i += 3) {
      const chunk = metasTraduzidas.slice(i, i + 3);
      const somaOptin = chunk.reduce((acc, item) => acc + (item?.optin || 0), 0);
      const somaVisitados = chunk.reduce((acc, item) => acc + (item?.visitados || 0), 0);
      const mesesStr = chunk.map(item => item?.mes).join('-');
      trimestres.push({
        trimestre: `T${Math.floor(i / 3) + 1} (${mesesStr})`,
        optin: somaOptin,
        visitados: somaVisitados
      });
    }
    return trimestres;
  };

  const trimestres = hasMetas ? calcularTrimestres() : [];

  const chartData = [];
  if (hasMetas) {
    chartData.push({
      id: "Meta Optin",
      color: "#5b9bd5",
      data: metasTraduzidas.map(r => ({ x: r.mes, y: r.optin }))
    });
    chartData.push({
      id: "Meta Visitados",
      color: "#198754",
      data: metasTraduzidas.map(r => ({ x: r.mes, y: r.visitados }))
    });
  }
  if (hasRealizado) {
    chartData.push({
      id: "Realizado Optin",
      color: "#ed7d31",
      data: realizadoTraduzido.map(r => ({ x: r.mes, y: r.optin }))
    });
    chartData.push({
      id: "Realizado Visitados",
      color: "#dc3545",
      data: realizadoTraduzido.map(r => ({ x: r.mes, y: r.visitados }))
    });
  }

  if (!hasMetas && !hasRealizado) {
    return (
      <Row>
        <Col md={12}>
          <Card className="border-0 shadow-sm">
            <Card.Header className="bg-white fw-bold border-0 d-flex justify-content-between align-items-center">
              <span>Quadro de Metas - {ano}</span>
              <Badge bg="secondary" className="fw-normal">{labelAno}</Badge>
            </Card.Header>
            <Card.Body>
              <div className="d-flex align-items-center justify-content-center py-5">
                <span className="text-muted">Sem registros de metas disponíveis</span>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    );
  }

  return (
    <>
      {/* Tabela de Metas Mensais - Título dinâmico */}
      {hasMetas && (
        <Row className="mb-4">
          <Col md={12}>
            <Card className="border-0 shadow-sm">
              <Card.Header className="bg-white fw-bold border-0 d-flex justify-content-between align-items-center">
                <span>Quadro de Metas - {ano}</span>
                <Badge bg="secondary" className="fw-normal">{labelAno}</Badge>
              </Card.Header>
              <Card.Body className="p-0">
                <Table responsive hover className="mb-0">
                  <thead className="table-light">
                    <tr>
                      <th className="fw-bold text-center" style={{ width: "180px" }}>Indicador</th>
                      {metasTraduzidas.map((item, idx) => (
                        <th key={idx} className="text-center">{item.mes}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td className="fw-semibold bg-light">
                        # Médicos Cadastros com Optin
                      </td>
                      {metasTraduzidas.map((item, idx) => (
                        <td key={idx} className="text-center">{item.optin}</td>
                      ))}
                    </tr>
                    <tr>
                      <td className="fw-semibold bg-light">
                        Médicos Visitados no Mês
                      </td>
                      {metasTraduzidas.map((item, idx) => (
                        <td key={idx} className="text-center">{item.visitados}</td>
                      ))}
                    </tr>
                  </tbody>
                </Table>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      )}

      {/* Tabela de Trimestres */}
      {trimestres.length > 0 && (
        <Row className="mb-4">
          <Col md={12}>
            <Card className="border-0 shadow-sm">
              <Card.Header className="bg-white fw-bold border-0 d-flex justify-content-between align-items-center">
                <span>Resumo por Trimestre</span>
                <Badge bg="secondary" className="fw-normal">{labelAno}</Badge>
              </Card.Header>
              <Card.Body className="p-0">
                <Table responsive hover className="mb-0">
                  <thead className="table-light">
                    <tr>
                      <th className="fw-bold text-center" style={{ width: "180px" }}>Indicador</th>
                      {trimestres.map((t, idx) => (
                        <th key={idx} className="text-center">{t.trimestre}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td className="fw-semibold bg-light">
                        Cadastros com Optin
                      </td>
                      {trimestres.map((t, idx) => (
                        <td key={idx} className="text-center fw-bold text-primary">{t.optin}</td>
                      ))}
                    </tr>
                    <tr>
                      <td className="fw-semibold bg-light">
                        Visitados no Período
                      </td>
                      {trimestres.map((t, idx) => (
                        <td key={idx} className="text-center fw-bold text-success">{t.visitados}</td>
                      ))}
                    </tr>
                  </tbody>
                </Table>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      )}

      {/* Gráfico de Linhas - Título dinâmico */}
      {chartData.length > 0 && (
        <Row>
          <Col md={12} className="mb-4">
            <Card className="border-0 shadow-sm">
              <Card.Header className="bg-white fw-bold border-0 d-flex justify-content-between align-items-center">
                <span>Metas {ano} - Projeção</span>
                <Badge bg="secondary" className="fw-normal">{labelAno}</Badge>
              </Card.Header>
              <Card.Body style={{ height: "380px" }}>
                <ResponsiveLine
                  data={chartData}
                  margin={{ top: 30, right: 30, bottom: 80, left: 60 }}
                  xScale={{ type: "point" }}
                  yScale={{ type: "linear", min: 0, max: "auto", stacked: false }}
                  axisBottom={{
                    tickSize: 5,
                    tickPadding: 5,
                    tickRotation: 0,
                    legend: "Mês",
                    legendOffset: 36,
                    legendPosition: "middle"
                  }}
                  axisLeft={{
                    tickSize: 5,
                    tickPadding: 5,
                    tickRotation: 0,
                    legend: "Quantidade",
                    legendOffset: -45,
                    legendPosition: "middle"
                  }}
                  colors={{ datum: "color" }}
                  pointSize={10}
                  pointColor={{ theme: "background" }}
                  pointBorderWidth={2}
                  pointBorderColor={{ from: "serieColor" }}
                  useMesh={true}
                  enableSlices="x"
                  legends={[
                    {
                      anchor: "bottom",
                      direction: "row",
                      justify: false,
                      translateX: 0,
                      translateY: 70,
                      itemsSpacing: 20,
                      itemDirection: "left-to-right",
                      itemWidth: 120,
                      itemHeight: 20,
                      itemOpacity: 0.85,
                      symbolSize: 12,
                      symbolShape: "circle"
                    }
                  ]}
                />
              </Card.Body>
            </Card>
          </Col>
        </Row>
      )}
    </>
  );
};

export default Slide9ProjecaoMetas;
